import React from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { openObjectiveDetailsPanelActionCreator } from 'actions/objectiveDetailsPanelActions';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { OkrProgress } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CommonTreeCell/ObjectiveProgressCell';
import { getMeasureUnitDetails } from 'screens/common/sidepanel/Goals/GoalProgress';
import { useReviewFormSidebarDataOld } from 'screens/performanceReview/components/Sidebar/hooks/dataHooks';
import { useReviewPreviewParams } from 'screens/performanceReview/hooks/reviewPreviewHooks';
import useReviewFormParams from 'screens/performanceReview/hooks/useReviewFormParams';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';

import { OpenInNew } from 'assets/assetsV1/iconComponents';

import { isMobileView } from 'constants/isMobileView';
import { sendOpenedKRPanelEvent } from 'constants/mixpanelHelper';

import { showTaskDetails } from 'services/taskDetails';

import { Button } from 'uiToolkitV1/Button';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

const TopBottomLabelValue = ({ label, value }) => (
  <Flex flexDir="column" mr={4}>
    <TextLabel color="text.g2" bold mb={1}>
      {label}
    </TextLabel>
    <TextBodyMedium bold>{value}</TextBodyMedium>
  </Flex>
);

const getAchievedValue = ({ orgCurrency = null, achieved, unit }) => {
  if (!orgCurrency) {
    // eslint-disable-next-line no-param-reassign
    orgCurrency = 'INR';
  }
  const { symbol: unitSymbol, isPrefix } = getMeasureUnitDetails(unit, orgCurrency);

  if (isPrefix) {
    return `${unitSymbol} ${achieved}`;
  }
  return `${achieved} ${unitSymbol}`;
};

const ReviewGoalSummary = ({ okr }) => {
  const dispatch = useDispatch();
  const isObjectiveType = okr?.type === 'OBJECTIVE';
  const { employeeId: empId, providerId, reviewId } = useReviewFormParams();
  const employeeObj = useSelector(selectLoggedInEmployeeData);
  const orgCurrency = employeeObj?.organization?.currency ?? null;
  const { isPreview } = useReviewPreviewParams();
  const history = useHistory();

  const { data: formData } = useReviewFormSidebarDataOld({
    employeeId: empId,
    providerId,
    reviewId,
  });

  const { isShowGoalWeightage } = formData ?? {};
  let weightageText = '-';
  if (okr?.weightage > 0) {
    weightageText = `${okr.weightage}%`;
  }

  const { keyResultsName } = useOrganisationSettings();

  const onGoalClickHandler = () => {
    sendOpenedKRPanelEvent({
      eventProperties: { source: 'Review Form', page: 'Review Form Page', type: 'Edit KR Panel' },
    });
    dispatch(showTaskDetails({ taskIds: [okr.uuid], type: 'GOAL', history }));
  };

  const onObjectiveClickHandler = () => {
    dispatch(
      openObjectiveDetailsPanelActionCreator({
        objectivePId: okr.uuid,
        history,
      })
    );
  };

  return (
    <Box bg="#F5F7F9" p="16px" flexDir="column" pt={3}>
      {Boolean(okr) && (
        <Flex justifyContent="space-between" alignItems="center" mb={6}>
          <TextBodyMedium bold>
            {!isObjectiveType && `${keyResultsName} Summary`}
            {isObjectiveType && `Objective Summary`}
          </TextBodyMedium>
          {!isMobileView() && (
            <Button
              StartIcon={OpenInNew}
              onClick={isObjectiveType ? onObjectiveClickHandler : onGoalClickHandler}
              variant="outlinedSmall"
              iconSize="16px"
              p="0 6px 0 0"
              isDisabled={isPreview}
            >
              {!isObjectiveType && `Open ${keyResultsName}`}
              {isObjectiveType && `Open Objective`}
            </Button>
          )}
        </Flex>
      )}

      {Boolean(okr && !isObjectiveType) && (
        <>
          <Flex justifyContent="space-between" alignItems="center" mb={4}>
            {isShowGoalWeightage && <TopBottomLabelValue label="Weightage" value={weightageText} />}
            <TopBottomLabelValue label="Target" value={okr?.target || '-'} />
            <TopBottomLabelValue
              label="Achieved"
              value={getAchievedValue({
                orgCurrency,
                achieved: okr?.achieved,
                unit: okr?.metric,
              })}
            />
          </Flex>
          <TextLabel color="text.g2" bold mb={1}>
            Metric
          </TextLabel>
          <TextBodyMedium mb={4}>{okr?.measureType}</TextBodyMedium>
          <Flex sx={{ width: '20rem' }} my={4} flexDir="column">
            <TextLabel color="text.g2" bold mb={2}>
              Progress
            </TextLabel>
            <OkrProgress data={okr} />
            {/* <RabbitGoalProgressBar
    goalProgressLevel={goal?.goalProgressLevel}
    percentageValue={Number(goal?.getGoalProgress(0) || 0)}
  /> */}
          </Flex>
        </>
      )}
      {Boolean(okr && isObjectiveType) && (
        <Flex justifyContent="space-between" alignItems="center" mb={4}>
          <Flex sx={{ width: '20rem' }} my={4} flexDir="column">
            <TextLabel color="text.g2" bold mb={2}>
              Progress
            </TextLabel>
            <OkrProgress data={okr} />
            {/* <RabbitGoalProgressBar
    goalProgressLevel={goal?.goalProgressLevel}
    percentageValue={Number(goal?.getGoalProgress(0) || 0)}
  /> */}
          </Flex>
          <TopBottomLabelValue label="Weightage" value={weightageText} />
        </Flex>
      )}
      {okr?.description && (
        <>
          <TextLabel color="text.g2" bold mb={1}>
            Description
          </TextLabel>
          <TextBodyMedium
            as="p"
            sx={{
              whiteSpace: 'pre-wrap',
              overflowX: 'scroll',
              display: '-webkit-box',
              '-webkit-line-clamp': '10',
              '-webkit-box-orient': 'vertical',
            }}
          >
            {okr?.description ?? '-'}
          </TextBodyMedium>
        </>
      )}
    </Box>
  );
};

export { ReviewGoalSummary };
